import { toast } from "react-hot-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AlertTriangle, X, FileText, Target, MessageSquare, ArrowRight } from "lucide-react";
import { useCancelOrganizationInvitation, useRemoveOrganizationMember } from "@/hooks/api-hooks";
import { getAvatarColor, getInitials } from "@/lib/utils";


interface Member {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatar?: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    avatar?: string;
  };
  role?: 'admin' | 'manager' | 'staff';
  organizationRole?: string;
  isActive?: boolean;
  isInvitation?: boolean;
}

interface DeleteMemberModalProps {
  member: Member | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: (memberId: string, options?: any) => void;
  organizationId: string;
}

export function DeleteMemberModal({
  member,
  isOpen,
  onClose,
  onConfirm,
  organizationId,
}: DeleteMemberModalProps) {
  const removeMemberMutation = useRemoveOrganizationMember();
  const cancelInvitationMutation = useCancelOrganizationInvitation();

  if (!member) return null;

  const firstName = member.user?.firstName || member.firstName || '';
  const lastName = member.user?.lastName || member.lastName || '';
  const email = member.user?.email || member.email || '';
  const avatar = member.user?.avatar || member.avatar;

  const handleDelete = async () => {
    if (member.isInvitation) {
      cancelInvitationMutation.mutate(
        {
          organizationId,
          invitationId: member.id,
        },
        {
          onSuccess: () => {
            toast.success(`Invitation for ${email} has been cancelled.`);
            onConfirm?.(member.id);
            onClose();
          },
          onError: (error: any) => {
            console.error("Error cancelling invitation:", error);
            toast.error(`Failed to cancel invitation: ${error?.message || 'Please try again.'}`);
          },
        }
      );
      return;
    }

    removeMemberMutation.mutate(
      {
        organizationId,
        memberId: member.id,
      },
      {
        onSuccess: () => {
          toast.success(`${firstName} ${lastName} removed from organization`);
          onConfirm?.(member.id);
          onClose();
        },
        onError: (error: any) => {
          console.error("Error removing member:", error);
          toast.error(`Failed to remove member: ${error?.message || 'Please try again.'}`);
        }
      }
    );
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader className="relative">
          <button
            onClick={handleClose}
            className="absolute right-0 top-0 p-2 rounded-md hover:bg-gray-100 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold">
                {member.isInvitation ? 'Cancel Invitation' : 'Remove Team Member'}
              </DialogTitle>
              <p className="text-sm text-muted-foreground">{firstName} {lastName}</p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* User Info Section */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <Avatar className="h-12 w-12">
              <AvatarImage src={avatar} alt={`${firstName} ${lastName}`} />
              <AvatarFallback className={`font-semibold ${getAvatarColor(firstName, lastName)}`}>
                {getInitials(firstName, lastName)}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium text-foreground">{firstName} {lastName}</div>
              <div className="text-sm text-muted-foreground">{email}</div>
            </div>
          </div>

          {/* Warning */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
              <div className="text-sm text-red-700">
                {member.isInvitation ? (
                  <>Canceling this invitation will prevent {email} from joining the organization unless a new invite is sent.</>
                ) : (
                  <>
                    <strong>Warning:</strong> This action cannot be undone. Removing a team member will permanently delete their account and access to the platform.
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Impact Summary */}
          <div>
            <h3 className="font-medium text-foreground mb-4">Impact Summary</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Target className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <div className="text-sm font-medium">1-on-1 Sessions</div>
                  <div className="text-lg font-semibold text-blue-600">0</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Target className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <div className="text-sm font-medium">Action Items</div>
                  <div className="text-lg font-semibold text-green-600">0</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <div className="text-sm font-medium">File Uploads</div>
                  <div className="text-lg font-semibold text-purple-600">0</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="h-4 w-4 text-orange-600" />
                </div>
                <div>
                  <div className="text-sm font-medium">Feedback Given</div>
                  <div className="text-lg font-semibold text-orange-600">0</div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={removeMemberMutation.isPending || cancelInvitationMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={removeMemberMutation.isPending || cancelInvitationMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white gap-2"
            >
              {removeMemberMutation.isPending || cancelInvitationMutation.isPending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
